import { useEffect, useRef, useState } from 'react';
import { Column } from '../pages/Components/Table/Types';
import { ImportacaoXMLCadastrar } from '../routes/RoutesNames';
import { useNavigate } from 'react-router-dom';
import { DatetimeBR } from '../shared/utils/helpers/date';
import { formatCurrency } from '../shared/utils/helpers/numbers';
import NotaFiscalController from '../controllers/NotaFiscalController';
import { ButtonMenu } from '../shared/components/ButtonMenu';
import { toast } from 'react-toastify';
import { useForm } from './useForm';
import { CartaCorrecaoCancelarNotaFiscalSchema } from '../schemas/CartaCorrecaoCancelarNotaFiscalSchema';
import { downloadBase64, openBase64InNewTab } from '../shared/utils/helpers/base64';
import Api from '../services/Api';
import GrupoClienteController from '../controllers/GrupoClienteController';
import { Item } from '../shared/components/InputSelect';
import { ParametrosRelatorio } from '../shared/components/ModalRelatorio';

interface Row {
  BtnExcluir?: JSX.Element;
  DataEmissao?: string;
  TipoArquivo?: string;
  Fornecedor?: string;
  Serie?: number;
  Id: number;
  ValorTotal?: string;
  BtnBaixar?: JSX.Element;
}

enum EventoNotaFiscalType {
  CANCELAR,
  CARTACORRECAO,
}

const ENABLE_TRANSMISSAO_STATUS = ['Não Transmitida', 'Rejeitada'];
const ENABLE_CANCELAR_CARTACORRECAO_STATUS = ['Transmitida'];

/* [29/09] Os status em que Excluir e permitido - os MESMOS da PROC_NOTA_FISCAL_EXCLUIR.
   'Nao Transmitida' nunca foi enviada; 'Rejeitada' foi e a SEFAZ recusou, entao o documento
   nao existe para ela. Transmitida, Cancelada e Processando ficam de fora, e a procedure
   explica por que quando alguem insiste. */
const ENABLE_EXCLUSAO_STATUS = ['Não Transmitida', 'Rejeitada'];
// Sincronizar so faz sentido para nota que ja foi ao servico e pode ter ficado com o
// retorno pendurado. 'Não Transmitida' fica de fora: nao existe status a consultar.
const DISABLE_SINCRONIZAR_STATUS = ['Não Transmitida'];

const useNotaFiscalPesquisar = () => {
  const navigate = useNavigate();
  const columns: Column[] = [
    { id: 'nfe_id', label: 'ID', align: 'center', width: 50, maxWidth: 100 },
    { id: 'nf_datainc', label: 'Data Criação', minWidth: 0, maxWidth: 100 },
    {
      id: 'nf_data_transmissao',
      label: 'Data Transmissão',
      minWidth: 0,
      maxWidth: 100,
    },
    { id: 'ide_n_nf', label: 'Número NF', minWidth: 0, maxWidth: 100 },
    { id: 'cli_nome', label: 'Cliente', minWidth: 0, maxWidth: 100 },
    { id: 'nf_status', label: 'Status', minWidth: 0, maxWidth: 100 },
    {
      id: 'nf_ultima_mensagem_service',
      label: 'Ultimo Status',
      minWidth: 0,
      maxWidth: 100,
    },
    {
      id: 'total_v_nf',
      label: 'Total NF',
      minWidth: 0,
      maxWidth: 100,
    },
    { id: 'BtnMenu', label: 'Ações', width: 60, minWidth: 0, maxWidth: 100 },
  ];
  const [rows, setRows] = useState<Row[]>([]);
  const [
    modalCancelarCartaCorrecao,
    setModalCancelarCartaCorrecaoCartaCorrecao,
  ] = useState(false);
  const [modalTitulo, setModalTitulo] = useState('');
  const [eventoNotaFiscalType, setEventoNotaFiscalType] =
    useState<EventoNotaFiscalType>();
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);
  const handleNavigation = () => {
    navigate(ImportacaoXMLCadastrar);
  };

  const onSubmit = async () => {
    setLoading(true);
    const result = await NotaFiscalController.listarNotasFiscais(
      // @ts-ignore - .
      formRef?.current?.values
    );

    if (!result) {
      setRows([]);
    }

    setLoading(false);
    setRows(
      result.map((item: any) => {
        return {
          nfe_id: item.nfe_id,
          nf_datainc: DatetimeBR.getFullFriendlyDate(item.nf_datainc, false),
          nf_data_transmissao: DatetimeBR.getFullFriendlyDate(
            item.nf_data_transmissao,
            false
          ),
          ide_n_nf: item.ide_n_nf,
          cli_nome: item.cli_nome,
          nf_status: item.nf_status,
          nf_ultima_mensagem_service: item.nf_ultima_mensagem_service,
          total_v_nf: formatCurrency(item.total_v_nf, 2, 2),
          BtnMenu: (
            <ButtonMenu
              items={[
                {
                  onClick: () => transmitirNota(item.nfe_id),
                  menu: 'Transmitir',
                  disabled: !ENABLE_TRANSMISSAO_STATUS.includes(item.nf_status),
                },
                {
                  onClick: () => sincronizarStatus(item.nfe_id),
                  menu: 'Sincronizar Status',
                  disabled: DISABLE_SINCRONIZAR_STATUS.includes(item.nf_status),
                },
                {
                  menu: 'Documentos',
                  children: [
                    /* O '100' fixo significa "o documento autorizado" - a DANFE. Nota
                       cancelada continua tendo, por isso ele não depende do status. */
                    {
                      menu: 'PDF',
                      onClick: () => downloadArquivo(item.nfe_id, 'PDF', '100'),
                    },
                    {
                      menu: 'XML',
                      onClick: () => downloadArquivo(item.nfe_id, 'XML', '100'),
                    },
                    /* [08/10] A carta de correção. Até hoje a tela só sabia EMITIR: o PDF
                       e o XML eram gravados e não havia por onde relê-los.

                       Vai por `origem` e NÃO por código: carta de correção e cancelamento
                       são os dois 135, e pedir "XML 135" de uma nota que tem os dois
                       devolveria qualquer um deles. */
                    {
                      menu: 'Carta de Correção (PDF)',
                      onClick: () =>
                        downloadArquivo(
                          item.nfe_id,
                          'PDF',
                          undefined,
                          'CartaCorrecao'
                        ),
                      disabled: item.tem_carta_correcao !== 'S',
                    },
                    {
                      menu: 'Carta de Correção (XML)',
                      onClick: () =>
                        downloadArquivo(
                          item.nfe_id,
                          'XML',
                          undefined,
                          'CartaCorrecao'
                        ),
                      disabled: item.tem_carta_correcao !== 'S',
                    },
                  ],
                },
                {
                  onClick: () => handleModalCartaCorrecaoOpen(item),
                  menu: 'Emitir Carta Correção',
                  disabled: !ENABLE_CANCELAR_CARTACORRECAO_STATUS.includes(
                    item.nf_status
                  ),
                },
                {
                  onClick: () => handleModalCancelarOpen(item),
                  menu: 'Cancelar',
                  disabled: !ENABLE_CANCELAR_CARTACORRECAO_STATUS.includes(
                    item.nf_status
                  ),
                },
                {
                  /* [29/09] Excluir a nota que nao foi transmitida.
                     Habilitado so nos status que a procedure aceita - o status esta na
                     coluna ao lado, entao nao e regra escondida. A recusa final continua
                     sendo dela, que devolve o motivo.
                     Confirmacao obrigatoria: some da listagem e devolve a venda para
                     ABERTO; nao e o tipo de coisa que se faz por clique errado. */
                  onClick: () => excluirNota(item),
                  menu: 'Excluir',
                  disabled: !ENABLE_EXCLUSAO_STATUS.includes(item.nf_status),
                  confirmar: `Excluir a nota fiscal ${item.ide_n_nf ?? item.nfe_id}? Ela sai da listagem e o pedido volta a poder ser faturado.`,
                },
              ]}
            />
          ),
        };
      })
    );
  };

  const cartaCorrecaoCancelarNota = async (data: any) => {
    if (data.nfe_id == null) return;
    if (eventoNotaFiscalType == null) return;

    setLoading(true);
    setModalCancelarCartaCorrecaoCartaCorrecao(false);
    let result = {
      success: false,
      message: '',
    };

    if (eventoNotaFiscalType == EventoNotaFiscalType.CANCELAR) {
      result = await NotaFiscalController.cancelarNotaFiscal(
        data.nfe_id,
        data.justificativa
      );
    }

    if (eventoNotaFiscalType == EventoNotaFiscalType.CARTACORRECAO) {
      result = await NotaFiscalController.cartaCorrecaoNotaFiscal(
        data.nfe_id,
        data.justificativa
      );
    }
    setLoading(false);

    formCancelarCartaCorrecao.resetForm();

    if (!result.success) {
      toast(result?.message ?? 'Houve algum erro. Verifique os dados!', {
        type: 'error',
      });
    }

    if (result.success) {
      toast(`Nota fiscal enviada para transmissão, aguarde transmissão!`, {
        type: 'success',
      });
      await onSubmit();
    }
  };

  const formCancelarCartaCorrecao = useForm({
    onSubmit: cartaCorrecaoCancelarNota,
    initialValues: {},
    validationSchema: CartaCorrecaoCancelarNotaFiscalSchema,
  });

  // [AJUSTE 12/08/2026] Dois defeitos que juntos produziam "loading infinito e nada
  // aconteceu" quando o servico de nota fiscal ficou inacessivel (12/08):
  //
  //   1. a mensagem lia `result.error.message`, campo que a API NUNCA manda (ela devolve
  //      Erro/ErroDetalhado), entao caia sempre no texto generico "Verifique os dados!" -
  //      que joga a culpa na nota quando o problema era o servico estar fora;
  //   2. o `setLoading(false)` estava no fim do corpo, fora de finally. Se o `onSubmit()`
  //      do ramo de sucesso lancasse, a excecao subia e o spinner ficava para SEMPRE.
  //      Esse era o unico caminho de loading realmente infinito aqui.
  /**
   * [29/09] Exclui a nota que não foi transmitida.
   *
   * Some da listagem e devolve o documento de origem: venda volta para ABERTO; devolução
   * continua FATURADO e perde só o vínculo — o faturamento dela vem da confirmação, e
   * voltá-la para aberto liberaria os mesmos itens para nova devolução.
   *
   * Quando a nota já tinha número, ele fica sem uso. A tela avisa, porque número pulado na
   * sequência é assunto fiscal e quem excluiu precisa saber que abriu esse buraco.
   */
  const excluirNota = async (item: any) => {
    setLoading(true);
    const result = await NotaFiscalController.excluirNotaFiscal(item.nfe_id);
    setLoading(false);

    if (!result.success) {
      return toast(result.mensagem, { type: 'error', autoClose: 15000 });
    }

    const numeroOrfao = result.dados?.[0]?.numero_orfao ?? result.dados?.numero_orfao;

    toast('Nota fiscal excluída.', { type: 'success' });

    if (numeroOrfao) {
      toast(
        `O número ${numeroOrfao} ficou sem uso na sequência de notas. Avise a contabilidade se for necessário inutilizá-lo.`,
        { type: 'warning', autoClose: 20000 }
      );
    }

    await onSubmit();
  };

  const transmitirNota = async (nfeId: number) => {
    setLoading(true);
    try {
      const result = await NotaFiscalController.transmitirNotaFiscal(nfeId);

      if (!result.success) {
        toast(result.mensagem ?? 'Houve algum erro. Verifique os dados!', {
          type: 'error',
          autoClose: 10000,
        });
        return;
      }

      await onSubmit();

      toast('Nota fiscal enviada para processamento!', {
        type: 'success',
      });
    } finally {
      setLoading(false);
    }
  };

  // Reconsulta a nota no servico e reaplica o retorno. Fica habilitado somente para
  // nota que JA foi transmitida (tem service_id): e o caso em que o retorno pode ter
  // se perdido. Nota nao transmitida nao tem o que sincronizar - para essa o caminho
  // e o Transmitir.
  const sincronizarStatus = async (nfeId: number) => {
    setLoading(true);
    const result = await NotaFiscalController.sincronizarStatus(nfeId);

    if (result.success) {
      await onSubmit();
      toast('Status sincronizado com o serviço de nota fiscal.', {
        type: 'success',
      });
    } else {
      toast(
        result?.message ??
          result?.error?.message ??
          'Não foi possível sincronizar o status. Verifique os dados!',
        { type: 'error' }
      );
    }
    setLoading(false);
  };

  const downloadArquivo = async (
    nfeId: number,
    tipo: string,
    statusCode?: string,
    origem?: string
  ) => {
    setLoading(true);
    const result = await NotaFiscalController.getNotaFiscalArquivo(
      nfeId,
      tipo,
      statusCode,
      origem
    );

    if (!result.success) {
      // [08/10] `message` primeiro: é onde a API explica o que faltou (ver o controller).
      toast(
        result?.message ??
          result?.error?.message ??
          'Houve algum erro. Verifique os dados!',
        { type: 'error' }
      );
    }

    if (result.success) {
      downloadBase64(
        result.data.base64,
        result.data.fileName,
        result.data.contentType
      );
    }
    setLoading(false);
  };

  const [loadingRelatorio, setLoadingRelatorio] = useState(false);
  // [29/09] O modal de parâmetros do relatório, e o que ele precisa saber.
  const [modalRelatorioAberto, setModalRelatorioAberto] = useState(false);
  const [periodoSugerido, setPeriodoSugerido] = useState({ inicio: '', fim: '' });
  const [setores, setSetores] = useState<Item[]>([]);
  const [loadingXmls, setLoadingXmls] = useState(false);

  /**
   * Baixa, num unico .zip, os XMLs das notas de SAIDA do periodo dos filtros.
   *
   * [23/10/2026 - card BUG ENVIO DOS XML] Passou a mandar tambem o STATUS. Antes so
   * iam as datas: o que a pessoa escolhesse em Status era ignorado, e o zip sempre
   * vinha so com as transmitidas. Nao e que o filtro funcionasse mal - ele nunca saia
   * da tela.
   *
   * O CLIENTE continua de fora, e isto e escolha, nao esquecimento: o arquivo vai para
   * a contabilidade, que precisa do periodo INTEIRO. Um zip filtrado por cliente
   * entregaria um mes incompleto com cara de completo, que e o pior defeito possivel
   * num arquivo fiscal.
   *
   * O limite de periodo vive NA PROC, nao aqui. A tela so avisa quando falta data, para
   * o usuario nao esperar a viagem inteira - se a regra morasse so aqui, uma chamada por
   * fora da tela passaria sem limite nenhum.
   *
   * A extensao do arquivo sai do CONTEUDO (todo zip comeca com "PK", que em base64 e
   * "UEsDB"), e nao do nome que o servidor mandou. E a mesma correcao do recibo: tela e
   * API podem estar em versoes diferentes sem gerar arquivo com extensao errada.
   */
  const downloadXmls = async () => {
    const v = formRef?.current?.values;

    if (!v?.DataInicio || !v?.DataFim) {
      return toast('Selecione Data Início e Data Fim para baixar os XMLs', { type: 'info' });
    }

    try {
      setLoadingXmls(true);

      const response = await Api.post('api/NotaFiscal/XmlsPorPeriodo', {
        DataInicio: v.DataInicio,
        DataFim: v.DataFim,
        Status: v.Status,
      });

      const d = response?.data;

      if (!d?.base64) {
        return toast('Nenhum XML foi gerado para o período.', { type: 'error' });
      }

      const ehZip = typeof d.base64 === 'string' && d.base64.startsWith('UEsDB');
      const nomeBase = (d.fileName || `xmls-nfe-${v.DataInicio}-a-${v.DataFim}`)
        .replace(/\.(zip|xml)$/i, '');

      downloadBase64(
        d.base64,
        `${nomeBase}.${ehZip ? 'zip' : 'xml'}`,
        ehZip ? 'application/zip' : 'application/xml'
      );
    } catch (e: any) {
      const erro = e?.response?.data;
      toast(
        erro?.ErroDetalhado || erro?.Erro || 'Não foi possível baixar os XMLs do período.',
        { type: 'error', autoClose: 10000 }
      );
    } finally {
      setLoadingXmls(false);
    }
  };

  /* [29/09] O botao Relatorio deixou de gerar direto e passou a abrir o modal de
     parametros, porque o Faturamento ganhou o filtro opcional de Setor (card
     [RELATORIOS]). As datas da tela vao pre-preenchidas: quem ja tinha o habito de
     filtrar e clicar continua vendo o que esperava, com uma confirmacao no meio. */
  const downloadRelatorio = () => {
    const v = formRef?.current?.values;
    setModalRelatorioAberto(true);
    // Sem datas na tela o modal abre vazio, e ele mesmo cobra o periodo.
    setPeriodoSugerido({ inicio: v?.DataInicio ?? '', fim: v?.DataFim ?? '' });
  };

  const fecharModalRelatorio = () => setModalRelatorioAberto(false);

  const gerarRelatorioFaturamento = async (parametros: ParametrosRelatorio) => {
    try {
      setLoadingRelatorio(true);

      const response = await Api.post(`api/relatorio/Faturamento`, {
        dataInicio: parametros.dataInicio,
        dataFim: parametros.dataFim,
        // Nulo = todos os setores. A procedure trata nulo e 0 do mesmo jeito, mas quem
        // le a requisicao no log merece ver a intencao, nao um zero ambiguo.
        setor: parametros.setor ?? null,
      });

      openBase64InNewTab(response.data.base64, "my-file", "application/pdf");
      setModalRelatorioAberto(false);
    }
    catch (e: any) {
      console.error(e);
      const erro = e?.response?.data;
      toast(erro?.ErroDetalhado || erro?.Erro || 'Erro ao baixar relatório',
            { type: 'error', autoClose: 10000 });
    }
    finally {
      setLoadingRelatorio(false);
    }
  }

  const handleModalCartaCorrecaoOpen = (item: any) => {
    setModalCancelarCartaCorrecaoCartaCorrecao(true);
    formCancelarCartaCorrecao.setFieldValue('nfe_id', item.nfe_id);
    formCancelarCartaCorrecao.setFieldValue('ide_n_nf', item.ide_n_nf);
    setModalTitulo('Carta Correcao Nota Fiscal');
    setEventoNotaFiscalType(EventoNotaFiscalType.CARTACORRECAO);
  };

  const handleModalCancelarOpen = (item: any) => {
    setModalCancelarCartaCorrecaoCartaCorrecao(true);
    formCancelarCartaCorrecao.setFieldValue('nfe_id', item.nfe_id);
    formCancelarCartaCorrecao.setFieldValue('ide_n_nf', item.ide_n_nf);
    setModalTitulo('Cancelar Nota Fiscal');
    setEventoNotaFiscalType(EventoNotaFiscalType.CANCELAR);
  };

  const handleCloseModalCancelar = () => {
    setModalCancelarCartaCorrecaoCartaCorrecao(false);
  };

  /* Os setores do cadastro, para o combo do modal. Lidos do GRUPOCLIENTE e nao fixos no
     codigo: o card lista quatro nomes, mas eles sao registros que alguem renomeia ou
     acrescenta sem avisar o desenvolvimento. Ver a PROC_REL_FATURAMENTO (script 132), que
     filtra por id pelo mesmo motivo.

     Falha na carga nao derruba nada: o combo fica vazio e o relatorio sai de todos os
     setores, que e o comportamento de sempre. */
  useEffect(() => {
    let ativo = true;

    GrupoClienteController.Listar({ UsuId: 0 }).then((resposta) => {
      if (!ativo || resposta.StatusCode !== 200 || !Array.isArray(resposta.Data)) return;

      setSetores(resposta.Data.map((g: any) => ({
        value: g.grucli_id,
        label: g.grucli_descricao,
      })));
    }).catch((e) => console.error('setores do relatorio', e));

    return () => { ativo = false; };
  }, []);

  useEffect(() => {
    onSubmit();
  }, []);

  return {
    loading,
    onSubmit,
    columns,
    rows,
    handleNavigation,
    formRef,
    modalCancelarCartaCorrecao,
    cancelarNota: cartaCorrecaoCancelarNota,
    handleCloseModalCancelar,
    formCancelarCartaCorrecao,
    modalTitulo,
    downloadRelatorio,
    loadingRelatorio,
    modalRelatorioAberto,
    fecharModalRelatorio,
    gerarRelatorioFaturamento,
    periodoSugerido,
    setores,
    downloadXmls,
    loadingXmls
  };
};

export default useNotaFiscalPesquisar;
